import { ICalloutContentStyles } from '@fluentui/react';
import { IPActionButton, IPSeparator, IPText, IPTooltipHost } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTarget } from 'baseComponents/Utils/Target';
import { globalStackTokensGapSmall } from 'globalStyles';
import KPI from 'models/kpi/kpi';
import KPIData from 'models/kpi/kpiData';
import TaskTypeFormElement, { TaskTypeFieldType } from 'models/tasks/taskTypeFormElement';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SingleFormElement from '../Tasks/Forms/SingleFormElement';

interface IKPIPreviewModalProps {
  kpi: KPI;
  kpis?: KPI[];
  isOpen: boolean;
  targetId: IPTarget;
  // Distance between the target and the callout body (Fluent also adds half the beak width).
  gapSpace?: number;
  allowSwitchView: boolean;
  onClose: () => void;
  onUpdateKPI: (kpi: KPI) => void;
  onUpdateValue: (kpi: KPI, data: KPIData) => void;
}

const CALLOUT_PADDING = 20;
const MAX_PREVIEW_VIEWPORT_RATIO = 0.9;
const PANEL_SETTLE_FRAME_COUNT = 32;
const PANEL_TOGGLE_GAP = -14;

const calloutStyles: Partial<ICalloutContentStyles> = {
  root: {
    padding: CALLOUT_PADDING,
  },
};

const getMaxPreviewHeight = (): number => {
  if (typeof window === 'undefined') return 600;

  return Math.round((window.innerHeight || 600) * MAX_PREVIEW_VIEWPORT_RATIO);
};

const isFormPreviewAnchor = (targetId: IPTarget): boolean =>
  typeof targetId === 'string' && targetId === 'fieldsPreviewAnchor';

const isPanelHostedTarget = (targetId: IPTarget): boolean => {
  if (typeof targetId !== 'string') return false;

  return targetId === 'kpiPreviewTarget' || targetId.startsWith('fieldPreviewToggle');
};

const resolveCalloutTarget = (targetId: IPTarget): IPTarget | undefined => {
  if (typeof document === 'undefined') return undefined;

  if (typeof targetId === 'string') {
    const byId = document.getElementById(targetId);
    if (byId) {
      if (targetId.startsWith('fieldPreviewToggle')) return byId;

      const toggle = byId.querySelector('[id^="fieldPreviewToggle"]') as HTMLElement | null;
      if (toggle) return toggle;

      return byId;
    }

    return `#${targetId}`;
  }

  if (typeof Element !== 'undefined' && targetId instanceof Element) {
    return targetId as HTMLElement;
  }

  return undefined;
};

const KPIPreviewModal = (props: IKPIPreviewModalProps) => {
  const { t } = useTranslation(['translation', 'kpis']);
  const [compact, setCompact] = useState<boolean>(true);
  const [formElement, setFormElement] = useState<TaskTypeFormElement | undefined>(undefined);
  const [maxPreviewHeight, setMaxPreviewHeight] = useState<number>(getMaxPreviewHeight);
  const [calloutTarget, setCalloutTarget] = useState<IPTarget | undefined>(undefined);

  const formAnchor = isFormPreviewAnchor(props.targetId);
  const panelHosted = isPanelHostedTarget(props.targetId);

  const directionalHint = formAnchor ? IPDirectionalHint.rightCenter : IPDirectionalHint.leftCenter;

  const gapSpace = useMemo(() => {
    if (props.gapSpace !== undefined) return props.gapSpace;
    if (panelHosted) return PANEL_TOGGLE_GAP;

    return formAnchor ? 8 : 30;
  }, [formAnchor, panelHosted, props.gapSpace]);

  const refreshCalloutTarget = useCallback(() => {
    setMaxPreviewHeight(getMaxPreviewHeight());
    setCalloutTarget(resolveCalloutTarget(props.targetId));
  }, [props.targetId]);

  useEffect(() => {
    const newElm = new TaskTypeFormElement();
    newElm.fieldType = TaskTypeFieldType.KPI;
    newElm.kpi = props.kpi;
    newElm.kpiId = props.kpi.kpiId;
    setFormElement(newElm);
  }, [props.kpi]);

  useEffect(() => {
    if (!props.isOpen) {
      setCompact(true);
      setCalloutTarget(undefined);

      return;
    }

    if (typeof window === 'undefined') return;

    let animationFrameId: number | undefined;
    let settleFrame = 0;

    const measureUntilSettled = () => {
      refreshCalloutTarget();
      settleFrame += 1;

      if (settleFrame < PANEL_SETTLE_FRAME_COUNT) {
        animationFrameId = window.requestAnimationFrame(measureUntilSettled);
      }
    };

    const restartMeasurements = () => {
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }

      settleFrame = 0;
      animationFrameId = window.requestAnimationFrame(measureUntilSettled);
    };

    restartMeasurements();

    window.addEventListener('resize', restartMeasurements);
    window.addEventListener('scroll', restartMeasurements, true);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(restartMeasurements) : undefined;
    const observed = resolveCalloutTarget(props.targetId);
    if (resizeObserver && observed instanceof HTMLElement) {
      resizeObserver.observe(observed);
    }

    return () => {
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }

      window.removeEventListener('resize', restartMeasurements);
      window.removeEventListener('scroll', restartMeasurements, true);
      resizeObserver?.disconnect();
    };
  }, [compact, props.isOpen, props.targetId, refreshCalloutTarget]);

  const entityCap = props.kpi.isField ? t('kpis:Entity.FieldCap') : t('kpis:Entity.KPI');
  const resolvedTarget =
    calloutTarget ??
    (typeof props.targetId === 'string' ? `#${props.targetId}` : props.targetId);

  return (
    <IPCallout
      hidden={!props.isOpen}
      styles={calloutStyles}
      directionalHint={directionalHint}
      gapSpace={gapSpace}
      beakWidth={20}
      calloutMaxHeight={maxPreviewHeight + CALLOUT_PADDING * 2}
      preventDismissOnResize
      preventDismissOnScroll
      target={resolvedTarget}
    >
      <IPStack
        tokens={globalStackTokensGapSmall}
        styles={{
          root: {
            width: compact ? 300 : 900,
            minWidth: 300,
            maxWidth: '80vw',
            maxHeight: maxPreviewHeight,
            overflowY: 'auto',
          },
        }}
      >
        <IPStack horizontal horizontalAlign="space-between" verticalAlign="center">
          <IPStack.Item>
            <IPText variant="large">
              {t('kpis:Dialogs.Preview.Title', {
                entityCap,
              })}
            </IPText>
          </IPStack.Item>
          <IPStack.Item>
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              {props.allowSwitchView && (
                <IPTooltipHost content={t('kpis:Dialogs.Preview.FullscreenTooltip')}>
                  <IPActionButton
                    text={t('kpis:Dialogs.Preview.FullscreenButton')}
                    iconProps={{ iconName: 'FullScreen' }}
                    checked={!compact}
                    onClick={() => setCompact((current) => !current)}
                  />
                </IPTooltipHost>
              )}
              <IPTooltipHost content={t('translation:General.Button.Close')}>
                <IPActionButton iconProps={{ iconName: 'Cancel' }} onClick={props.onClose} />
              </IPTooltipHost>
            </IPStack>
          </IPStack.Item>
        </IPStack>
        <IPSeparator styles={{ root: { height: 1 } }} />
        {formElement && (
          <IPStack.Item>
            <SingleFormElement
              field={formElement}
              kpis={props.kpis}
              compact={compact}
              readonly={false}
              designTime={true}
              onUpdateValue={props.onUpdateValue}
              onUpdateKPI={props.onUpdateKPI}
              onSetKPI={props.onUpdateKPI}
              allowEdit={false}
              addLinks={() => {}}
              setLinks={() => {}}
              removeLink={() => {}}
              links={[]}
            />
          </IPStack.Item>
        )}
      </IPStack>
    </IPCallout>
  );
};

export default KPIPreviewModal;
